/**
 * C6-B · 1f — a espera mínima para unificar propostas, em Configurações.
 *
 * ── Por que este número existe
 *
 * O Matheus enxergou o atalho: criar dois cards e unificar na hora renderia
 * **2 propostas geradas por um negócio só**. O cooldown não impede — cobra
 * tempo, e é o tempo que tira o ganho de fazer isso de propósito.
 *
 * ── Por que a tela explica antes de deixar mudar
 *
 * Um campo "dias" sozinho não diz o que acontece se virar 0 nem se virar 90.
 * Quem abre esta tela daqui a seis meses não estava na conversa, e um número
 * sem contexto vira mudança feita no escuro.
 */
import { useEffect, useState } from 'react';

import { Button, Input, Label, useToast } from '@/components/ui';
import { api } from '@/lib/api';
import { mensagemDaRecusa, useCooldownUnificacao } from '@/lib/crm/unificacao-store';
import { useQueryClient } from '@tanstack/react-query';

export function CooldownUnificacaoTab() {
  const { data, isLoading } = useCooldownUnificacao();
  const [dias, setDias] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const toast = useToast();
  const qc = useQueryClient();

  useEffect(() => {
    if (data) setDias(String(data.dias));
  }, [data]);

  async function salvar() {
    setErro(null);
    setSalvando(true);
    try {
      await api.put('/api/v1/crm/unificacoes/config/cooldown', { dias: Number(dias) });
      await qc.invalidateQueries({ queryKey: ['v1', 'crm', 'unificacoes', 'cooldown'] });
      toast.show('Espera mínima salva.', { variant: 'success' });
    } catch (e) {
      setErro(mensagemDaRecusa(e) ?? 'Não foi possível salvar agora.');
    } finally {
      setSalvando(false);
    }
  }

  if (isLoading) return <p className="text-sm text-ink-3 dark:text-ink-dark-3">Carregando…</p>;

  return (
    <div className="max-w-xl space-y-4">
      <p className="text-sm text-ink-2 dark:text-ink-dark-2">
        Quantos dias uma proposta precisa ter <strong>desde que foi criada</strong> para poder
        entrar numa unificação. Vale para cada proposta escolhida, não para o intervalo entre elas.
      </p>

      <div className="space-y-1.5">
        <Label htmlFor="cooldown-dias">Espera mínima (dias corridos)</Label>
        <Input
          id="cooldown-dias"
          type="number"
          min={0}
          max={90}
          value={dias}
          onChange={(e) => setDias(e.target.value)}
          className="w-32"
          data-testid="cooldown-dias"
        />
      </div>

      <div className="rounded-xl border border-line dark:border-line-dark p-3 text-sm text-ink-2 dark:text-ink-dark-2 space-y-1.5">
        <p>
          <strong>Por que existe:</strong> sem espera, dá para criar duas propostas e unificar na
          hora — o mês fecharia com duas propostas geradas para um negócio só.
        </p>
        <p>
          <strong>Zero desliga a espera.</strong> A unificação passa a valer a qualquer momento.
        </p>
        <p>
          Quem for barrado vê o motivo: <em>&quot;Este card tem 2 dias. A unificação exige{' '}
          {dias || '7'} dias.&quot;</em>
        </p>
      </div>

      {erro && (
        <p role="alert" className="rounded-xl border border-red/40 bg-red/5 p-3 text-red font-medium text-sm">
          {erro}
        </p>
      )}

      <Button onClick={salvar} disabled={salvando} data-testid="cooldown-salvar">
        {salvando ? 'Salvando…' : 'Salvar'}
      </Button>
    </div>
  );
}
